import React, { useState, useEffect } from 'react';

import { Card, CardContent, Stack, Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useLabelValue } from '../../utils/useLabelValue';
import { AccBadge } from '../Badge/AccBadge';
import { LengthBadgeFeature } from '../Badge/LengthBadge';

export const CardInfoGraph = ({ selectedGraph }) => {
  const { displayType } = useLabelValue();
  const { t } = useTranslation();

  const {
    displaySurfaceType,
    displaySmoothnessLevel,
    displayKerbDesign,
    displayPedestrianLights,
    displayZebracrossing,
    displayTactileWarningStrip,
  } = useLabelValue();

  const [graphProperties, setGraphProperties] = useState({});

  const style = {
    card: {
      display: 'flex',
      width: '90%',
      paddingX: '.2em',
    },
    cardContent: {
      display: 'flex',
      width: '100%',
    },
    labelCard: {
      fontSize: '1.1em',
    },
    labelPart: {
      fontSize: '0.8em',
    },
  };

  useEffect(() => {
    setGraphProperties(selectedGraph.properties);
    return () => {
      setGraphProperties({});
    };
  }, [selectedGraph]);

  const RowInfo = ({ label, children }) => {
    return (
      <Stack
        sx={{ display: 'flex', width: '100%', alignItems: 'center' }}
        component='li'
        spacing={2}
        direction='row'
      >
        <Typography sx={style.labelPart}>{label} :</Typography>
        {children}
      </Stack>
    );
  };

  const calcWidth = (value) => {
    const valueNumber = Number(value).toFixed(1);
    if (valueNumber > 2) {
      return t('stepPath.more_2');
    } else {
      return `${valueNumber} m`;
    }
  };

  const renderInfo = () => {
    return (
      <Stack
        sx={{ display: 'flex', width: '100%', paddingLeft: 0, paddingRight: 0 }}
        component='ul'
        spacing={2}
        direction='column'
      >
        {graphProperties.name && (
          <Box sx={{ display: 'flex', width: '100%' }}>
            <Typography sx={style.labelCard}>{graphProperties.name}</Typography>
          </Box>
        )}
        <RowInfo label='Type'>
          <Typography sx={style.labelPart}>
            {displayType(graphProperties.type)}
          </Typography>
        </RowInfo>
        <RowInfo label={t('stepPath.label-access')}>
          <AccBadge acc={graphProperties.acc} />
        </RowInfo>
        <RowInfo label={t('stepPath.label-length')}>
          <LengthBadgeFeature feature={selectedGraph} />
        </RowInfo>
        <RowInfo label={t('stepPath.label-width')}>
          <Typography sx={style.labelPart}>
            {graphProperties.width || graphProperties.width === 0 ? calcWidth(graphProperties.width) : t('stepPath.label-unknown')}
          </Typography>
        </RowInfo>
        <RowInfo label={t('stepPath.label-slope')}>
          <Typography sx={style.labelPart}>
            {graphProperties.slope || graphProperties.slope === 0 ? `${Math.abs(Number(graphProperties.slope).toFixed(1))} %` : t('stepPath.label-unknown')}
          </Typography>
        </RowInfo>
        <RowInfo label={t('stepPath.label-tilt')}>
          <Typography sx={style.labelPart}>
            {graphProperties.tilt || graphProperties.tilt === 0 ? `${Math.abs(Number(graphProperties.tilt).toFixed(1))} %` : t('stepPath.label-unknown')}
          </Typography>
        </RowInfo>
        <RowInfo label={t('stepPath.label-surface')}>
          <Typography sx={style.labelPart}>
            {displaySurfaceType(graphProperties.surface)}
          </Typography>
        </RowInfo>
        <RowInfo label={t('stepPath.label-smoothness')}>
          <Typography sx={style.labelPart}>
            {displaySmoothnessLevel(graphProperties.smoothness)}
          </Typography>
        </RowInfo>
        {graphProperties.type == 'crosswalk' && (
          <>
            <RowInfo label={t('stepPath.label-kerbdesign')}>
              <Typography sx={style.labelPart}>
                {displayKerbDesign(graphProperties.kerbdesign)}
              </Typography>
            </RowInfo>
            <RowInfo label={t('stepPath.label-pedestrianlights')}>
              <Typography sx={style.labelPart}>
                {displayPedestrianLights(graphProperties.pedestrianlights)}
              </Typography>
            </RowInfo>
            <RowInfo label={t('stepPath.label-zebracrossing')}>
              <Typography sx={style.labelPart}>
                {displayZebracrossing(graphProperties.zebracrossing)}
              </Typography>
            </RowInfo>
            <RowInfo label={t('stepPath.label-tactilewarningstrip')}>
              <Typography sx={style.labelPart}>
                {displayTactileWarningStrip(
                  graphProperties.tactilewarningstrip,
                )}
              </Typography>
            </RowInfo>
          </>
        )}
      </Stack>
    );
  };

  return (
    <Card sx={style.card}>
      <CardContent sx={style.cardContent}>{renderInfo()}</CardContent>
    </Card>
  );
};
